import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type DiffLinkToken,
  documentDiffToVersionDiff,
  type Kind,
  lineDiffToVersionDiff,
  type VersionDiff,
  type VersionTick,
} from "@norkron/web-review-workbench";
import { articleApi, type Section } from "../api/articleUnits";
import { parseDiffScope, resourceScope, sectionScope } from "./diffScope";
import {
  compareOpts,
  resolveTick,
  sectionHeaderLabel,
  sectionVersionsToTicks,
  seedAB,
  tagSectionGroup,
} from "./versionCompare";

/** Per-kind whole-resource A/B pins (the timeline pin-A/pin-B gestures write
 *  these; the resource tray reads + edits them). */
type ResourcePins = Partial<Record<Kind, { a?: string; b?: string }>>;

/** The two compare-tray scopes. A `resource` tray edits the shared `ResourcePins`
 *  (so the timeline highlight + the tray stay in lock-step); a `section` tray owns
 *  its own lazily-fetched stream + A/B. */
type TrayState =
  | { scope: "resource"; kind: Kind }
  | {
      scope: "section";
      kind: Kind;
      section: Section;
      ticks: VersionTick[];
      a?: string;
      b?: string;
    };

/** The presentational shape `<CompareTray>` renders — resolved from whichever
 *  tray is open. */
export interface TrayView {
  readonly title: string;
  readonly ticks: readonly VersionTick[];
  readonly selectedA?: string;
  readonly selectedB?: string;
}

/**
 * The CONTEXT of the currently-open `<SideBySideDiff>` — everything needed to (a)
 * render the in-header version switcher (`ticks` / `aId` / `bId`), (b) re-run the
 * compare for a NEW pair in place (`recompare`), and (c) serialize a `?diff=`
 * permalink for the open comparison (`scope` + `aId` + `bId`, design §2.3-A). It is
 * set whenever a compare opens (tray "Diff", a deep-link, or an in-header switch)
 * and cleared when the diff closes.
 */
export interface CompareCtx {
  /** The elaborate `?diff=` token scope (`resource:<kind>` | `section:<id>`). */
  readonly scope: string;
  readonly kind: Kind;
  /** Set for a section-scoped compare (the section whose stream is compared). */
  readonly section?: Section;
  readonly sectionLabel?: string;
  /** The version list the in-header switcher renders (already ordered). */
  readonly ticks: VersionTick[];
  /** The currently-compared base (A) / compare (B) version ids. */
  readonly aId: string;
  readonly bId: string;
}

/** The spec `runCompareFor` executes — the union of a resource + section compare. */
interface CompareSpec {
  readonly scope: string;
  readonly kind: Kind;
  readonly section?: Section;
  readonly sectionLabel?: string;
  readonly ticks: VersionTick[];
  readonly aId: string;
  readonly bId: string;
}

export interface UseVersionCompare {
  /** Per-kind A/B pins (for the timeline `selectedA`/`selectedB` highlight). */
  readonly pins: ResourcePins;
  /** Timeline shift-click → pin A on a resource's version. */
  readonly setResourceA: (kind: Kind, versionId: string) => void;
  /** Timeline alt-click → pin B on a resource's version. */
  readonly setResourceB: (kind: Kind, versionId: string) => void;
  /** Header "Compare" → open the whole-resource tray (head pre-pinned as B). */
  readonly openResourceTray: (kind: Kind) => void;
  /** Section-chip Compare → open a section-scoped tray (stream seeded oldest→newest). */
  readonly openSectionTray: (kind: Kind, section: Section) => Promise<void>;
  /** Tray "Set A" — routes to the resource pins or the section tray. */
  readonly setTrayA: (versionId: string) => void;
  /** Tray "Set B" — routes to the resource pins or the section tray. */
  readonly setTrayB: (versionId: string) => void;
  /** Tray "Swap" — flip A↔B in the open tray's scope. */
  readonly swapTray: () => void;
  /** Tray "Diff" — fetch the picked A→B compare + open `<SideBySideDiff>`. */
  readonly runCompare: () => Promise<void>;
  /** Close the tray without diffing. */
  readonly closeTray: () => void;
  /** The resolved props for the open tray (or null when none is open). */
  readonly trayView: TrayView | null;
  /** The A→B diff model to feed `<SideBySideDiff>` (or null when closed). */
  readonly compareModel: VersionDiff | null;
  /** The open comparison's context (switcher options + A/B + `?diff=` scope). */
  readonly compareCtx: CompareCtx | null;
  /** True while an in-place re-compare (version switch / deep-link open) is in flight. */
  readonly loading: boolean;
  /** In-header version switch: re-fetch the OPEN comparison for a new (A,B) pair,
   *  keeping `<SideBySideDiff>` mounted (only its `model` swaps). */
  readonly recompare: (aId: string, bId: string) => Promise<void>;
  /** Open the compare a `?diff=<token>` encodes on a cold load (resource scope). */
  readonly openCompareFromToken: (token: DiffLinkToken) => Promise<boolean>;
  /** Close the diff overlay. */
  readonly closeDiff: () => void;
}

/**
 * `useVersionCompare` — the W4 "pick any two versions" compare state machine
 * (design §2.7 + §5). Owns the per-resource A/B pins (timeline gestures), the whole-
 * resource + section-scoped compare trays, the resulting `<SideBySideDiff>` model,
 * AND the open comparison's CONTEXT (so the in-header switcher can re-compare a new
 * pair in place and a `?diff=` permalink can be serialized for it). It composes the
 * EXISTING compare terminals via `articleApi` (`compareResource` / `compareSection`)
 * and the lib's pure bridge (`documentDiffToVersionDiff` / `lineDiffToVersionDiff`) —
 * it adds NO routes and NO lib code. Kept OUT of `<ArticleWorkbench>` so that screen
 * stays thin.
 *
 * @param unitId  the article unit id (compareResource keys on it)
 * @param ticks   the per-kind whole-resource version streams (timeline ticks)
 */
export function useVersionCompare(
  unitId: string,
  ticks: Record<Kind, VersionTick[]>,
  onError: (message: string) => void,
): UseVersionCompare {
  const [pins, setPins] = useState<ResourcePins>({});
  const [tray, setTray] = useState<TrayState | null>(null);
  const [compareModel, setCompareModel] = useState<VersionDiff | null>(null);
  const [compareCtx, setCompareCtx] = useState<CompareCtx | null>(null);
  const [loading, setLoading] = useState(false);

  // Latest-ctx ref so `recompare` (fired by the lib's `onCompare` callback) can read
  // the OPEN comparison without being re-created on every ctx change.
  const compareCtxRef = useRef<CompareCtx | null>(null);
  compareCtxRef.current = compareCtx;

  // A fresh unit resets ALL compare state (pins, tray, overlay, ctx).
  useEffect(() => {
    setPins({});
    setTray(null);
    setCompareModel(null);
    setCompareCtx(null);
    setLoading(false);
  }, [unitId]);

  const setResourceA = useCallback((kind: Kind, versionId: string) => {
    setPins((p) => ({ ...p, [kind]: { ...(p[kind] ?? {}), a: versionId } }));
  }, []);
  const setResourceB = useCallback((kind: Kind, versionId: string) => {
    setPins((p) => ({ ...p, [kind]: { ...(p[kind] ?? {}), b: versionId } }));
  }, []);

  const openResourceTray = useCallback(
    (kind: Kind) => {
      // Pre-pin head as B (+ prev as A) only for sides not already pinned via the
      // timeline — never clobber a reviewer's explicit pick.
      setPins((p) => {
        const cur = p[kind] ?? {};
        if (cur.a !== undefined && cur.b !== undefined) return p;
        const seeded = seedAB(ticks[kind] ?? [], "adjacent");
        return {
          ...p,
          [kind]: { a: cur.a ?? seeded.a, b: cur.b ?? seeded.b },
        };
      });
      setTray({ scope: "resource", kind });
    },
    [ticks],
  );

  const openSectionTray = useCallback(
    async (kind: Kind, section: Section) => {
      let versions = section.versions;
      if (versions.length < 2) {
        try {
          versions = await articleApi.sectionVersions(section.id);
        } catch {
          // fall through with whatever the section already carries
        }
      }
      const secTicks = sectionVersionsToTicks(
        versions.length > 0 ? versions : section.versions,
      );
      if (secTicks.length === 0) {
        onError("No versions to compare for this section");
        return;
      }
      const seeded = seedAB(secTicks, "span");
      setTray({
        scope: "section",
        kind,
        section,
        ticks: secTicks,
        a: seeded.a,
        b: seeded.b,
      });
    },
    [onError],
  );

  const setTrayA = useCallback(
    (versionId: string) => {
      setTray((t) => {
        if (t === null) return t;
        if (t.scope === "section") return { ...t, a: versionId };
        setResourceA(t.kind, versionId);
        return t;
      });
    },
    [setResourceA],
  );
  const setTrayB = useCallback(
    (versionId: string) => {
      setTray((t) => {
        if (t === null) return t;
        if (t.scope === "section") return { ...t, b: versionId };
        setResourceB(t.kind, versionId);
        return t;
      });
    },
    [setResourceB],
  );

  const swapTray = useCallback(() => {
    setTray((t) => {
      if (t === null) return t;
      if (t.scope === "section") return { ...t, a: t.b, b: t.a };
      setPins((p) => {
        const cur = p[t.kind] ?? {};
        return { ...p, [t.kind]: { a: cur.b, b: cur.a } };
      });
      return t;
    });
  }, []);

  // The ONE fetch+lower seam every open path funnels through (tray Diff, in-header
  // switch, deep-link landing). Fetches the picked A→B compare over the EXISTING
  // terminal, lowers it via the lib bridge, and records the `compareCtx`. Returns
  // whether it opened (false + onError on a bad/equal pair or a fetch failure).
  const runCompareFor = useCallback(
    async (spec: CompareSpec): Promise<boolean> => {
      const from = resolveTick(spec.ticks, spec.aId);
      const to = resolveTick(spec.ticks, spec.bId);
      if (from === undefined || to === undefined || from.versionId === to.versionId) {
        onError("Pick two different versions to compare");
        return false;
      }
      const opts = compareOpts(spec.kind, from, to);
      setLoading(true);
      try {
        let model: VersionDiff;
        if (spec.section !== undefined) {
          const diff = await articleApi.compareSection(
            spec.section.id,
            from.versionId,
            to.versionId,
          );
          const base =
            spec.kind === "research"
              ? lineDiffToVersionDiff(diff, opts)
              : documentDiffToVersionDiff(diff, opts);
          model = tagSectionGroup(
            base,
            spec.sectionLabel ?? sectionHeaderLabel(spec.section),
          );
        } else {
          const diff = await articleApi.compareResource(
            unitId,
            spec.kind,
            from.versionId,
            to.versionId,
          );
          model =
            spec.kind === "research"
              ? lineDiffToVersionDiff(diff, opts)
              : documentDiffToVersionDiff(diff, opts);
        }
        setCompareModel(model);
        setCompareCtx({
          scope: spec.scope,
          kind: spec.kind,
          section: spec.section,
          sectionLabel: spec.sectionLabel,
          ticks: spec.ticks,
          aId: from.versionId,
          bId: to.versionId,
        });
        return true;
      } catch {
        onError("Couldn't compare these versions");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [unitId, onError],
  );

  const runCompare = useCallback(async () => {
    if (tray === null) return;
    const resolvedTicks =
      tray.scope === "section" ? tray.ticks : (ticks[tray.kind] ?? []);
    const aId = tray.scope === "section" ? tray.a : pins[tray.kind]?.a;
    const bId = tray.scope === "section" ? tray.b : pins[tray.kind]?.b;
    const scope =
      tray.scope === "section"
        ? sectionScope(String(tray.section.id))
        : resourceScope(tray.kind);
    const ok = await runCompareFor({
      scope,
      kind: tray.kind,
      section: tray.scope === "section" ? tray.section : undefined,
      sectionLabel:
        tray.scope === "section" ? sectionHeaderLabel(tray.section) : undefined,
      ticks: resolvedTicks,
      aId: aId ?? "",
      bId: bId ?? "",
    });
    if (ok) setTray(null);
  }, [tray, ticks, pins, runCompareFor]);

  const recompare = useCallback(
    async (aId: string, bId: string) => {
      const ctx = compareCtxRef.current;
      if (ctx === null) return;
      // The lib's switcher already suppresses an equal pair; belt + suspenders here.
      if (aId === bId) return;
      await runCompareFor({
        scope: ctx.scope,
        kind: ctx.kind,
        section: ctx.section,
        sectionLabel: ctx.sectionLabel,
        ticks: ctx.ticks,
        aId,
        bId,
      });
    },
    [runCompareFor],
  );

  const openCompareFromToken = useCallback(
    async (token: DiffLinkToken): Promise<boolean> => {
      const parsed = parseDiffScope(token.scope);
      // Cold-load landing supports the whole-resource compare (the version list is
      // `ticks[kind]`, already in hand). A section-scoped token needs the section
      // object + its lazily-fetched stream, which this hook does not hold on a cold
      // load → degrade gracefully to "not opened" (mirrors the deep-link tolerance).
      if (parsed === null || parsed.kind !== "resource") return false;
      const kind = parsed.role;
      const resolvedTicks = ticks[kind] ?? [];
      if (resolvedTicks.length === 0) return false;
      return runCompareFor({
        scope: token.scope,
        kind,
        ticks: resolvedTicks,
        aId: token.aVersionId,
        bId: token.bVersionId,
      });
    },
    [ticks, runCompareFor],
  );

  const closeTray = useCallback(() => setTray(null), []);
  const closeDiff = useCallback(() => {
    setCompareModel(null);
    setCompareCtx(null);
  }, []);

  const trayView = useMemo<TrayView | null>(() => {
    if (tray === null) return null;
    if (tray.scope === "section") {
      return {
        title: `Compare ${sectionHeaderLabel(tray.section)}`,
        ticks: tray.ticks,
        selectedA: tray.a,
        selectedB: tray.b,
      };
    }
    const cur = pins[tray.kind] ?? {};
    return {
      title: `Compare ${tray.kind}`,
      ticks: ticks[tray.kind] ?? [],
      selectedA: cur.a,
      selectedB: cur.b,
    };
  }, [tray, pins, ticks]);

  return {
    pins,
    setResourceA,
    setResourceB,
    openResourceTray,
    openSectionTray,
    setTrayA,
    setTrayB,
    swapTray,
    runCompare,
    closeTray,
    trayView,
    compareModel,
    compareCtx,
    loading,
    recompare,
    openCompareFromToken,
    closeDiff,
  };
}
